import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Linkedin, Mail, Loader2, Send } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import SEO from "@/components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Modernization Assurance",
  "Full Modernization",
  "Application Development",
  "Cloud Services",
  "IT Consulting & Staffing",
  "DevOps & Automation",
  "Data & AI Engineering",
  "Legacy Testing Suite (Product)",
  "AI Usage Analytics (Product)",
  "Something else",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || form.message.length < 5) {
      toast.error("Please complete name, email and a short message (5+ chars).");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data?.success) {
        setSubmitted(true);
        toast.success("Message received. We'll reply shortly.");
        setForm({ name: "", email: "", company: "", service_interest: "", message: "" });
      } else {
        toast.error("Unexpected response. Please try again.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact | Start a Project"
        description="Start a conversation with United Techlab. Every enquiry is read by the founder first. Typically responded within 24 hours."
        path="/contact"
      />
      <PageHeader
        eyebrow="> /contact"
        title="Tell us what you're building."
        subtitle="Every enquiry is read by the founder first. Expect a thoughtful, human reply, usually within 24 hours on business days."
      />

      <section className="bg-obsidian border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="border border-white/10 bg-carbon p-8">
              <div className="chip text-neon-green">// direct_line</div>
              <h3 className="mt-3 font-display font-bold text-white text-2xl">
                Prefer email?
              </h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">
                You can also reach us directly, or send a LinkedIn DM to the
                founder. We reply quickly.
              </p>

              <div className="mt-6 space-y-3">
                <a
                  href="mailto:contact@unitedtechlab.com"
                  className="flex items-center gap-3 border border-white/10 px-4 py-3 hover:border-neon-green hover:text-neon-green transition-colors"
                  data-testid="contact-email-link"
                >
                  <Mail size={16} />
                  <span className="font-mono text-sm">contact@unitedtechlab.com</span>
                </a>
                <a
                  href="https://www.linkedin.com/in/jspeedluk/"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 border border-white/10 px-4 py-3 hover:border-neon-green hover:text-neon-green transition-colors"
                  data-testid="contact-linkedin-link"
                >
                  <Linkedin size={16} />
                  <span className="font-mono text-sm">linkedin.com/in/jspeedluk</span>
                </a>
              </div>

              <div className="mt-8 border-t border-white/10 pt-6 space-y-2 font-mono text-xs text-white/50">
                <div><span className="text-neon-green">$</span> response_time: &lt;24h</div>
                <div><span className="text-neon-green">$</span> read_by: founder_first</div>
                <div><span className="text-neon-green">$</span> privacy: strict</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div
                className="border border-neon-green/50 bg-carbon p-12 text-center"
                data-testid="contact-success"
              >
                <div className="chip text-neon-green">// transmission_received</div>
                <h3 className="mt-4 font-display font-black text-3xl text-white">
                  Message on its way.
                </h3>
                <p className="mt-3 text-sm text-white/60 max-w-md mx-auto">
                  Thanks, we'll be in touch within 24 hours. In the meantime,
                  feel free to look at our services or products.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-8 inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 font-mono text-sm text-white hover:border-neon-green hover:text-neon-green transition-colors"
                  data-testid="contact-send-another"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="border border-white/10 bg-carbon p-8 lg:p-10 space-y-6"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Name" required>
                    <input
                      data-testid="contact-field-name"
                      value={form.name}
                      onChange={onChange("name")}
                      className="input-neon"
                      placeholder="Jane Doe"
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      data-testid="contact-field-email"
                      value={form.email}
                      onChange={onChange("email")}
                      type="email"
                      className="input-neon"
                      placeholder="jane@company.com"
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Company">
                    <input
                      data-testid="contact-field-company"
                      value={form.company}
                      onChange={onChange("company")}
                      className="input-neon"
                      placeholder="Acme Corp"
                    />
                  </Field>
                  <Field label="Service / Product interest">
                    <div className="relative">
                      <select
                        data-testid="contact-field-service"
                        value={form.service_interest}
                        onChange={onChange("service_interest")}
                        className="input-neon appearance-none pr-10"
                      >
                        <option value="">Select one...</option>
                        {SERVICE_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neon-green font-mono">▾</span>
                    </div>
                  </Field>
                </div>
                <Field label="Message" required>
                  <textarea
                    data-testid="contact-field-message"
                    value={form.message}
                    onChange={onChange("message")}
                    rows={6}
                    className="input-neon resize-none"
                    placeholder="Tell us what you're working on, the scope, timelines, and anything we should know..."
                  />
                </Field>

                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <p className="text-xs font-mono text-white/40 max-w-xs">
                    By sending, you agree to our{" "}
                    <a href="/privacy" className="text-white/70 underline-offset-4 hover:text-neon-green">
                      privacy policy
                    </a>
                    .
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-form-submit"
                    className="group inline-flex items-center gap-2 bg-neon-green text-obsidian px-6 py-3 text-sm font-mono font-bold uppercase tracking-widest hover:shadow-[0_0_40px_rgba(0,255,65,0.6)] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending
                      </>
                    ) : (
                      <>
                        Send Message <Send size={16} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .input-neon {
          width: 100%;
          background: #050505;
          border: 1px solid rgba(255,255,255,0.14);
          color: #fff;
          padding: 12px 14px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .input-neon::placeholder { color: rgba(255,255,255,0.3); }
        .input-neon:focus {
          border-color: #00FFFF;
          box-shadow: 0 0 0 1px #00FFFF, 0 0 22px rgba(0,255,255,0.25);
        }
        select.input-neon option { background: #050505; color: #fff; }
      `}</style>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="chip text-white/60">
        {label} {required && <span className="text-neon-green">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
